import React, { useState, useEffect, useCallback } from 'react';
import { studentApi } from '../studentApi';
import { ACTIVE_CHAT_POLLING_INTERVAL, MESSAGES_PER_PAGE } from '../../../constants';
import ChatList from './components/ChatList';
import ChatMessages from './components/ChatMessages';

function Inbox() {
    const [selectedChat, setSelectedChat] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [chatrooms, setChatrooms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [mobileView, setMobileView] = useState('list');
    
    // Pagination states
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [messagesOffset, setMessagesOffset] = useState(0);

    useEffect(() => {
        return () => {
            if (window.chatListInterval) clearInterval(window.chatListInterval);
            if (window.messageInterval) clearInterval(window.messageInterval);
        };
    }, []);

    const fetchMessages = useCallback(async (chatroomId, chatName, isInitialLoad = false, offset = 0, append = false) => {
        if (!chatroomId) return;
        
        try {
            if (isInitialLoad) {
                setLoadingMessages(true);
                setMessagesOffset(0);
            } else if (append) {
                setLoadingMoreMessages(true);
            }
            
            // Ensure offset is a number
            const numericOffset = Number(offset);
            const response = await studentApi.getChatroomMessages(chatroomId, MESSAGES_PER_PAGE, numericOffset);
            
            // Handle new response structure with pagination
            const messagesData = response?.data?.messages || response?.data || [];
            const pagination = response?.data?.pagination || {};
            
            if (!messagesData || messagesData.length === 0) {
                if (isInitialLoad) {
                    setMessages([]);
                    setHasMoreMessages(false);
                }
                setMessageError('');
                return;
            }
            
            const formattedMessages = messagesData.map(message => ({
                id: message.id,
                sender: message.sender_role || 'unknown',
                senderName: message.sender_name,
                content: message.content,
                timestamp: message.timestamp,
                isRead: message.is_read,
                readAt: message.read_at,
                senderTimezone: message.sender_timezone || null
            }));
            
            // Update messages - append if loading more, replace if initial load
            if (append) {
                // Prepend older messages to the beginning, filtering out duplicates
                setMessages(prevMessages => {
                    const existingIds = new Set(prevMessages.map(m => m.id));
                    const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
                    return [...newMessages, ...prevMessages];
                });
                // Update offset to the next position (current offset + number of messages loaded)
                setMessagesOffset(prevOffset => prevOffset + formattedMessages.length);
                setHasMoreMessages(pagination.has_more || false);
            } else {
                // Replace messages on initial load or refresh
                setMessages(formattedMessages);
                // Set offset to the number of messages we just loaded
                setMessagesOffset(formattedMessages.length);
                setHasMoreMessages(pagination.has_more || false);
            }
            
            setMessageError('');
        } catch (err) {
            console.error('Failed to fetch messages:', err);
            setMessageError('Failed to load messages');
            if (isInitialLoad) {
                setMessages([]);
            }
        } finally {
            if (isInitialLoad) {
                setLoadingMessages(false);
            }
            if (append) {
                setLoadingMoreMessages(false);
            }
        }
    }, []);

    const fetchChatrooms = useCallback(async () => {
        try {
            setLoading(true);
            const response = await studentApi.getChatrooms();
            console.log('Student chatrooms response:', response);
            console.log('Student chatrooms results:', response.results);
            if (response.results && response.results.length > 0) {
                console.log('First chatroom data:', response.results[0]);
                if (response.results[0].last_message) {
                    console.log('Last message timestamp:', response.results[0].last_message.timestamp);
                }
                console.log('Created at:', response.results[0].created_at);
            }
            const formattedChats = (response.results || []).map(chat => ({
                id: chat.id,
                name: chat.other_user_name,
                lastMessage: chat.last_message ? chat.last_message.content : 'No messages yet',
                lastMessageTime: chat.last_message && chat.last_message.timestamp ? 
                    new Date(chat.last_message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '',
                unread: chat.unread_count,
                unreadCount: chat.unread_count,
                role: 'staff',
                avatar: chat.other_user_name ? chat.other_user_name.charAt(0).toUpperCase() : 'S',
                avatarColor: '#3B82F6', // Blue color for staff
                isOnline: true, // Assume staff is online
                subject: chat.prospect_details?.subject,
                grade: chat.prospect_details?.grade,
                notes: chat.prospect_details?.notes,
                demoDate: chat.prospect_details?.demo_date,
                demoTime: chat.prospect_details?.demo_time,
                timezone: chat.prospect_details?.timezone,
                dateTime: new Date(chat.created_at).toLocaleDateString(),
                leads: chat.leads || null
            }));
            
            console.log('Formatted chats:', formattedChats);
            if (formattedChats.length > 0) {
                console.log('First formatted chat role:', formattedChats[0].role);
                console.log('First formatted chat name:', formattedChats[0].name);
            }
            setChatrooms(formattedChats);
            
            if (selectedChat) {
                const updatedSelectedChat = formattedChats.find(chat => chat.id === selectedChat.id);
                if (updatedSelectedChat) {
                    setSelectedChat(updatedSelectedChat);
                }
            } else if (formattedChats.length > 0 && !selectedChat) {
                setSelectedChat(formattedChats[0]);
            }
            
            // Clear any previous errors
            setError(null);
        } catch (err) {
            console.error('Failed to fetch chatrooms:', err);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    }, [selectedChat]);

    // Fetch chatrooms on component mount only
    useEffect(() => {
        fetchChatrooms();
    }, []);

    // Fetch messages when selected chat changes
    useEffect(() => {
        if (!selectedChat) return;

        // Initial load of last messages (based on MESSAGES_PER_PAGE)
        fetchMessages(selectedChat.id, selectedChat.name, true, 0, false);

        // Poll for unread messages only
        const pollInterval = setInterval(async () => {
            try {
                const response = await studentApi.getUnreadMessages(selectedChat.id);
                if (response.data && response.data.length > 0) {
                    // Only re-fetch last messages if there are new unread messages
                    await fetchMessages(selectedChat.id, selectedChat.name, false, 0, false);
                }
            } catch (error) {
                console.error('Error polling for unread messages:', error);
            }
        }, ACTIVE_CHAT_POLLING_INTERVAL);

        return () => clearInterval(pollInterval);
    }, [selectedChat, fetchMessages]);
    
    // Handler to load more messages
    const handleLoadMoreMessages = useCallback(() => {
        if (!selectedChat || loadingMoreMessages || !hasMoreMessages) return;
        
        // Use the current offset (which represents how many messages we've already loaded)
        // This will fetch the next batch of older messages
        fetchMessages(selectedChat.id, selectedChat.name, false, messagesOffset, true);
    }, [selectedChat, messagesOffset, loadingMoreMessages, hasMoreMessages, fetchMessages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedChat) return;
        
        try {
            setSendingMessage(true);
            await studentApi.sendMessage(selectedChat.id, newMessage.trim());
            setNewMessage('');
            // Refresh to show the latest messages including the new one
            await fetchMessages(selectedChat.id, selectedChat.name, false, 0, false);
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setSendingMessage(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const handleMessageChange = (e) => {
        setNewMessage(e.target.value);
    };

    useEffect(() => {
        if (selectedChat) {
            setMobileView('messages');
        } else {
            setMobileView('list');
        }
    }, [selectedChat]);

    // Set default selected chat to the first chatroom when available
    useEffect(() => {
        if (!selectedChat && chatrooms.length > 0) {
            setSelectedChat(chatrooms[0]);
        }
    }, [chatrooms, selectedChat]);

    return (
        <div className="h-screen bg-gray-50 p-4">
            <div className="h-[calc(100vh-2rem)] flex bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Left Panel - Chat List */}
                <div className={`w-80 bg-white flex flex-col h-full ${mobileView === 'list' ? 'block' : 'hidden md:flex'}`}>
                    <ChatList 
                        chatrooms={chatrooms}
                        selectedChat={selectedChat}
                        onChatSelect={(chat) => setSelectedChat(chat)}
                        loading={loading}
                        error={error}
                    />
                </div>

                {/* Middle Panel - Chat Messages */}
                <div className={`flex-1 bg-white flex flex-col h-full ${mobileView === 'messages' ? 'block' : 'hidden md:flex'}`}>
                    {/* Mobile Header */}
                    <div className="md:hidden flex items-center gap-2 p-3 border-b border-gray-200 bg-white">
                        {selectedChat && (
                            <button 
                                onClick={() => setMobileView('list')}
                                className="text-blue-600 font-semibold px-3 py-2"
                            >
                                ← Back
                            </button>
                        )}
                        {selectedChat && (
                            <div className="flex-1 font-semibold">
                                <span>{selectedChat.name}</span>
                            </div>
                        )}
                    </div>

                    <ChatMessages 
                        selectedChat={selectedChat}
                        messages={messages}
                        loadingMessages={loadingMessages}
                        messageError={messageError}
                        newMessage={newMessage}
                        sendingMessage={sendingMessage}
                        onMessageChange={handleMessageChange}
                        onKeyDown={handleKeyDown}
                        onSendMessage={handleSendMessage}
                        hasMoreMessages={hasMoreMessages}
                        loadingMoreMessages={loadingMoreMessages}
                        onLoadMore={handleLoadMoreMessages}
                    />
                </div>
            </div>
        </div>
    );
}

export default Inbox;